import { Avatar } from '@amelio/ui-library';
import { useCallback } from 'react';

import type { User } from '@/types/directory/User';

import { useGetUsersByIds } from '@/api/directory';
import OverlayLoader from '@/components/OverlayLoader/OverlayLoader';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import {
    fullName,
    hasName,
    initials,
    jobAndDepartmentLabel,
    MUTED,
    NAVY,
} from '@/pages/Competencies/components/employees/personDisplay';
import { getProfilePictureUrl } from '@/utils/user';

type AssignedEmployeesListProps = {
    // The employee ids resolved for the selected section (a profile's assignees or the
    // individually-added employees). Their identities are resolved here from the directory.
    employeeIds: string[];
    // Stabilizes the directory cache key for this drill-in (e.g. the profile id or "individual").
    cacheIdentifier: string;
    // Optional trailing per-row action (e.g. the assigned-employees modal's unassign button).
    renderRowAction?: (user: User) => React.ReactNode;
};

export const AssignedEmployeesList = ({
    employeeIds,
    cacheIdentifier,
    renderRowAction,
}: AssignedEmployeesListProps) => {
    const { t } = useAmelioTranslations();
    const { getJobTitle, getDepartment } = useCompanyInformation();

    const {
        data: users,
        isLoading,
        isError,
    } = useGetUsersByIds({ userIds: employeeIds, identifier: cacheIdentifier });

    // Secondary tile line: localized "Job title · Department", resolved from the ids the
    // directory user already carries. Omits a missing side and falls back to a placeholder.
    const jobAndDepartment = useCallback(
        (user: User): string =>
            jobAndDepartmentLabel(user, {
                getJobTitle,
                getDepartment,
                fallback: t('competencies.assignedProfilesModal.noJobDepartment'),
            }),
        [getJobTitle, getDepartment, t],
    );

    if (employeeIds.length === 0) {
        return (
            <p
                className={'py-10 text-center text-base'}
                style={{ color: MUTED }}
            >
                {t('competencies.assignedProfilesModal.employeesEmpty')}
            </p>
        );
    }

    if (isLoading) {
        return (
            <div className={'flex h-[200px] items-center justify-center'}>
                <OverlayLoader isLoading={isLoading} />
            </div>
        );
    }

    if (isError || !users) {
        return (
            <p className={'text-error py-10 text-center text-base'}>
                {t('competencies.assignedProfilesModal.employeesError')}
            </p>
        );
    }

    return (
        <div className={'flex flex-col gap-3'}>
            {users.map((user) => (
                <div
                    key={user.id}
                    className={
                        'flex items-center gap-3 rounded-xl border border-[#dbe0e6] bg-white p-4'
                    }
                    data-testid={`assigned-profiles-employee-${user.id}`}
                >
                    <Avatar
                        size={'sm'}
                        src={getProfilePictureUrl(user.profilePictureUrl) || undefined}
                        bgColor={'primary'}
                        initials={initials(user)}
                        alt={fullName(user)}
                    />

                    <div className={'min-w-0 flex-1'}>
                        <p
                            className={'truncate text-base font-semibold'}
                            style={{ color: NAVY }}
                        >
                            {hasName(user) ? fullName(user) : t('common.anonymous')}
                        </p>

                        <p
                            className={'truncate text-sm'}
                            style={{ color: MUTED }}
                        >
                            {jobAndDepartment(user)}
                        </p>
                    </div>

                    {renderRowAction?.(user)}
                </div>
            ))}
        </div>
    );
};
